import { Routes, Route, useLocation } from 'react-router-dom';
import { Suspense, lazy, useState, useEffect, Component, type ErrorInfo, type ReactNode } from 'react';
import { isDemoMode, disableDemoMode } from './demo';
import { usePWA } from './hooks/usePWA';
import PWAPrompt from './components/PWAPrompt';
import OfflineIndicator from './components/OfflineIndicator';
import MobileNavigation from './components/MobileNavigation';
import LanguageSelector from './components/LanguageSelector';
import { useTranslation } from './hooks/useTranslation';
import DemoBanner from './demo/DemoBanner';

// Simple components to avoid complex loading
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Login = lazy(() => import('./pages/auth/Login'));
const Register = lazy(() => import('./pages/auth/Register'));
const OAuthCallback = lazy(() => import('./pages/auth/OAuthCallback'));
const AuthConfig = lazy(() => import('./pages/admin/AuthConfig'));
const UserManagement = lazy(() => import('./pages/admin/UserManagement'));
const DemoEntry = lazy(() => import('./pages/DemoEntry'));
const Equipment = lazy(() => import('./pages/Equipment'));
const EquipmentDetail = lazy(() => import('./pages/EquipmentDetail'));
const EditEquipment = lazy(() => import('./pages/EditEquipment'));
const Scan = lazy(() => import('./pages/Scan'));
const Voice = lazy(() => import('./pages/Voice'));
const Reports = lazy(() => import('./pages/Reports'));
const Settings = lazy(() => import('./pages/Settings'));
const ImportExport = lazy(() => import('./pages/ImportExport'));

// Route-level error boundary: a crash in one page must degrade to an inline
// error card, never white-screen the whole app shell.
class RouteErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Route crashed:', error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-xl mx-auto text-center">
            <h2 className="text-lg font-semibold text-red-800 mb-2">Something went wrong on this page</h2>
            <p className="text-sm text-red-700 mb-4">
              The rest of the app is unaffected. You can go back to the dashboard or try reloading.
            </p>
            <div className="flex justify-center gap-3">
              <a
                href="/app/dashboard"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
              >
                Back to Dashboard
              </a>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
              >
                Reload
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Simple Protected Route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const token = localStorage.getItem('accessToken');
  if (!token) {
    return <Login />;
  }
  return <>{children}</>;
};

// Simple Layout component
const AppLayout = () => {
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const { capabilities } = usePWA();
  const { t } = useTranslation();
  
  const handleLogout = () => {
    // Leaving the demo must fully tear it down, or the demo flag (and its
    // banner) survives onto the real /login page.
    if (isDemoMode()) {
      disableDemoMode();
      return;
    }
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    window.location.href = '/';
  };

  const isAdminPage = location.pathname.includes('/admin');

  return (
    <div className="min-h-screen bg-gray-50 pb-16 md:pb-0">
      {/* Desktop Navigation */}
      <nav className="hidden md:block bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center space-x-8">
              <h1 className="text-xl font-semibold text-gray-900">ETrax</h1>
              <div className="flex space-x-4">
                <a href="/app/dashboard" className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium">
                  {t('common.dashboard')}
                </a>
                <a href="/app/equipment" className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium">
                  {t('equipment.title')}
                </a>
                <a href="/app/scan" className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium">
                  {t('equipment.scanQR')}
                </a>
                <a href="/app/voice" className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium">
                  {t('voice.title')}
                </a>
                <a href="/app/reports" className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium">
                  {t('reports.title')}
                </a>
                <a href="/app/settings" className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium">
                  {t('common.settings')}
                </a>
                <a href="/app/import-export" className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium">
                  {t('common.import')}/{t('common.export')}
                </a>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {(user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') && (
                <div className="flex items-center space-x-3">
                  <a href="/app/admin/auth" className="text-blue-600 hover:text-blue-800 text-sm">
                    {t('admin.authConfig')}
                  </a>
                  <a href="/app/admin/users" className="text-blue-600 hover:text-blue-800 text-sm">
                    {t('admin.userManagement')}
                  </a>
                </div>
              )}
              <LanguageSelector />
              <span className="text-gray-700 hidden lg:block">
                {t('dashboard.welcome', { name: user?.firstName || user?.name || user?.email })}
              </span>
              <button onClick={handleLogout} className="text-red-600 hover:text-red-800">
                {t('common.logout')}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Header */}
      <header className="md:hidden bg-white shadow-sm border-b border-gray-200">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <h1 className="text-lg font-semibold text-gray-900">ETrax</h1>
            <div className="flex items-center space-x-3">
              {(user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') && !isAdminPage && (
                <a href="/app/admin/auth" className="text-blue-600 hover:text-blue-800 text-sm">
                  {t('admin.title')}
                </a>
              )}
              <button
                onClick={handleLogout}
                className="text-red-600 hover:text-red-800 text-sm"
              >
                {t('common.logout')}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Offline Indicator */}
      <OfflineIndicator isOnline={capabilities.isOnline} />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-4 px-4 md:py-6 md:px-6 lg:px-8">
        <RouteErrorBoundary key={location.pathname}>
          <Routes>
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="equipment" element={<Equipment />} />
            <Route path="equipment/:id" element={<EquipmentDetail />} />
            <Route path="equipment/:id/edit" element={<EditEquipment />} />
            <Route path="scan" element={<Scan />} />
            <Route path="voice" element={<Voice />} />
            <Route path="reports" element={<Reports />} />
            <Route path="settings" element={<Settings />} />
            <Route path="import-export" element={<ImportExport />} />
            <Route path="admin/auth" element={<AuthConfig />} />
            <Route path="admin/users" element={<UserManagement />} />
            <Route path="" element={<Dashboard />} />
            <Route path="*" element={<Dashboard />} />
          </Routes>
        </RouteErrorBoundary>
      </main>

      {/* Mobile Navigation */}
      {!isAdminPage && <MobileNavigation currentPath={location.pathname} />}
    </div>
  );
};

const AppRoutes = () => (
  <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
    <Routes>
      {/* Root route for unauthenticated users */}
      <Route path="/" element={<Login />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/demo" element={<DemoEntry />} />
      <Route path="/auth/callback" element={<OAuthCallback />} />
      
      {/* Protected app routes */}
      <Route path="/app/*" element={<ProtectedRoute><AppLayout /></ProtectedRoute>} />
    </Routes>
  </Suspense>
);

function App() {
  const [showPWAPrompt, setShowPWAPrompt] = useState(false);
  const { capabilities, installApp, addToHomeScreen } = usePWA();

  useEffect(() => {
    // Show PWA install prompt after a delay if app is installable
    const timer = setTimeout(() => {
      if (capabilities.isInstallable && !capabilities.isInstalled) {
        setShowPWAPrompt(true);
      }
    }, 30000); // Show after 30 seconds

    return () => clearTimeout(timer);
  }, [capabilities.isInstallable, capabilities.isInstalled]);

  const handleInstallApp = async () => {
    const success = await installApp();
    if (success) {
      setShowPWAPrompt(false);
    }
    return success;
  };

  const handleDismissPWAPrompt = () => {
    setShowPWAPrompt(false);
    // Don't show again for 7 days
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  // Check if PWA prompt was recently dismissed
  useEffect(() => {
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed);
      const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
      if (dismissedTime > sevenDaysAgo) {
        setShowPWAPrompt(false);
        return;
      }
    }
  }, []);

  const homeScreenInstructions = addToHomeScreen();

  return (
    <div className="min-h-screen bg-gray-50">
      <AppRoutes />
      <DemoBanner />
      
      {showPWAPrompt && (
        <PWAPrompt
          onInstall={handleInstallApp}
          onDismiss={handleDismissPWAPrompt}
          showIOSInstructions={homeScreenInstructions.showInstructions}
          iosInstructions={homeScreenInstructions.instructions}
        />
      )}
    </div>
  );
}

export default App;